import { lazy, ComponentType, LazyExoticComponent } from 'react';

/**
 * Centralized routes file - Single source of truth for all lazy-loaded components
 *
 * Helper function to create lazy-loaded components with preload support
 * This wraps React.lazy and adds a .preload() method for code splitting optimization
 */
function lazyWithPreload<T extends ComponentType<Record<string, never>>>(
  importFn: () => Promise<{ default: T }>,
): LazyExoticComponent<T> & { preload: () => void } {
  const LazyComponent = lazy(importFn) as LazyExoticComponent<T> & { preload?: () => void };

  // Add preload method that triggers the import immediately
  LazyComponent.preload = () => {
    importFn();
  };

  return LazyComponent as LazyExoticComponent<T> & { preload: () => void };
}

// ============================================================================
// Main Page Components (with preload support)
// ============================================================================

export const MonitorV2 = lazyWithPreload(() => import('./pages/monitor/monitor'));
export const Analyze = lazyWithPreload(() => import('../app/pages/analyze/analyze'));
export const AnalyzeV2 = lazyWithPreload(() => import('./pages/analyze/analyze'));
export const Investigate = lazyWithPreload(() => import('./pages/investigate/investigate'));
export const AssessV2 = lazyWithPreload(() => import('./pages/assess'));
export const AlertBoardV2 = lazyWithPreload(() => import('./pages/alertV2'));
export const AlertManager = lazyWithPreload(() => import('./pages/alertManager'));
export const ManagePage = lazyWithPreload(() => import('./pages/manage'));
export const ViewPage = lazyWithPreload(() => import('./pages/views'));

// ============================================================================
// Legacy/App-v1 Components
// ============================================================================

export const LimitsV2 = lazyWithPreload(() => import('../app/pages/limits/limitsV2'));
export const TimeSearch = lazyWithPreload(() => import('../app/pages/time-search/time-search'));
export const AlertPage = lazyWithPreload(() => import('../app/pages/alert/alert'));
export const Share = lazyWithPreload(() => import('../app/pages/share/share'));
export const ShareV2 = lazyWithPreload(() => import('./pages/share/share'));
export const ShareCollection = lazyWithPreload(() => import('./pages/share/collection'));
export const AlertViewV2 = lazyWithPreload(() => import('./pages/alertView/alertView'));

// ============================================================================
// Manage Sub-Pages
// ============================================================================

export const ManageMonitors = lazy(() => import('./pages/manage/monitors'));
export const ManageAlerts = lazy(() => import('./pages/manage/alerts'));
export const ManageUsers = lazy(() => import('./pages/manage/users'));
export const ManageWebhooks = lazy(() => import('./pages/manage/webhooks'));

// ============================================================================
// Layout & UI Components
// ============================================================================

export const ViewControls = lazy(() =>
  import('./pages/views/view-controls').then((module) => ({ default: module.ViewControls })),
);

export const PortalMenuProvider = lazy(() =>
  import('./components/system-explorer/assess-menu-context').then((module) => ({
    default: module.PortalMenuProvider,
  })),
);

export const TabContentByMode = lazy(() =>
  import('./components/layout/tab-layout/TabContentByMode').then((module) => ({
    default: module.TabContentByMode,
  })),
);

export const TimeRangePicker = lazy(() =>
  import('./components/time-range-picker/views-time-range-picker').then((module) => ({
    default: module.default,
  })),
);

export const TimelineActions = lazy(() =>
  import('./components/timeline-actions/timeline-actions').then((module) => ({
    default: module.TimelineActions,
  })),
);
